import { useState } from "react";
import { Check, Sparkles } from "lucide-react";
import { Reveal } from "./Reveal";

const TIERS = [
  {
    id: "pricing-card-semilla",
    name: "Semilla",
    monthly: 0,
    desc: "Para empezar a ordenar tus cobros.",
    features: ["Hasta 15 recordatorios/mes", "1 usuario", "Recordatorios por email", "Panel básico"],
  },
  {
    id: "pricing-card-crecimiento",
    name: "Crecimiento",
    monthly: 14,
    desc: "Para Mypes que cobran cada semana.",
    featured: true,
    features: [
      "Recordatorios ilimitados",
      "WhatsApp con tu nombre y tono",
      "Links de pago (Yape, Plin, MP)",
      "Semáforo de riesgo de cliente",
      "Soporte por chat en español",
    ],
  },
  {
    id: "pricing-card-pro",
    name: "Pro Empresa",
    monthly: 29,
    desc: "Para equipos y operaciones grandes.",
    features: [
      "Todo lo de Crecimiento",
      "Multi-usuario y roles",
      "Integraciones API",
      "Gestor de cuenta dedicado",
    ],
  },
];

export const Pricing = () => {
  const [annual, setAnnual] = useState(false);

  return (
    <section id="precios" data-testid="pricing-section" className="mx-auto max-w-6xl px-4 py-24 sm:px-6 lg:py-32">
      <Reveal className="text-center">
        <p className="mono mb-4 text-xs uppercase tracking-[0.2em] text-emerald-400">Precios</p>
        <h2 className="font-display mx-auto max-w-2xl text-2xl font-bold leading-snug tracking-tight sm:text-3xl lg:text-4xl">
          Menos de lo que cuesta una factura perdida.
        </h2>

        <div className="mt-8 inline-flex items-center rounded-xl border border-white/10 bg-white/[0.03] p-1">
          <button
            data-testid="pricing-toggle-monthly"
            onClick={() => setAnnual(false)}
            className={`rounded-lg px-5 py-2 text-sm font-medium transition-colors duration-300 ${
              !annual ? "bg-emerald-500 text-[#0A0A0C]" : "text-slate-400 hover:text-slate-200"
            }`}
          >
            Mensual
          </button>
          <button
            data-testid="pricing-toggle-annual"
            onClick={() => setAnnual(true)}
            className={`flex items-center gap-2 rounded-lg px-5 py-2 text-sm font-medium transition-colors duration-300 ${
              annual ? "bg-emerald-500 text-[#0A0A0C]" : "text-slate-400 hover:text-slate-200"
            }`}
          >
            Anual
            <span className={`mono rounded-full px-2 py-0.5 text-[10px] ${annual ? "bg-[#0A0A0C]/20 text-[#0A0A0C]" : "bg-emerald-500/15 text-emerald-400"}`}>
              -20%
            </span>
          </button>
        </div>
      </Reveal>

      <div className="mt-14 grid gap-5 lg:grid-cols-3">
        {TIERS.map((t, i) => {
          const price = annual ? Math.round(t.monthly * 0.8) : t.monthly;
          return (
            <Reveal key={t.id} delay={i * 0.1} className="h-full">
              <article
                data-testid={t.id}
                className={`relative flex h-full flex-col rounded-3xl border p-8 transition-all duration-500 hover:-translate-y-1.5 ${
                  t.featured
                    ? "border-emerald-500/50 bg-gradient-to-b from-emerald-500/10 to-[#131316] shadow-[0_30px_80px_-30px_rgba(16,185,129,0.35)]"
                    : "border-white/10 bg-[#131316]/70 hover:border-white/20"
                }`}
              >
                {t.featured && (
                  <span className="absolute -top-3.5 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-emerald-500 px-4 py-1 text-xs font-bold text-[#0A0A0C]">
                    <Sparkles className="h-3 w-3" /> Recomendado
                  </span>
                )}
                <h3 className="font-display text-xl font-semibold tracking-tight">{t.name}</h3>
                <p className="mt-1 text-sm text-slate-400">{t.desc}</p>
                <div className="mt-6 flex items-end gap-2">
                  <span className="font-display text-5xl font-black tracking-tight">
                    ${price}
                  </span>
                  <span className="pb-1.5 text-sm text-slate-500">USD/mes</span>
                </div>
                {annual && t.monthly > 0 && (
                  <p className="mono mt-1 text-[11px] text-emerald-400">Facturado anual · ahorras ${t.monthly * 12 - price * 12}</p>
                )}
                <ul className="mt-7 flex-1 space-y-3">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                      <Check className={`mt-0.5 h-4 w-4 shrink-0 ${t.featured ? "text-emerald-400" : "text-slate-500"}`} />
                      {f}
                    </li>
                  ))}
                </ul>
                <a
                  data-testid={t.featured ? "pricing-select-tier-button" : `${t.id}-button`}
                  href="#waitlist"
                  className={`mt-8 inline-flex h-12 items-center justify-center rounded-xl text-sm font-semibold transition-transform duration-300 hover:scale-[1.02] active:scale-[0.98] ${
                    t.featured
                      ? "bg-gradient-to-br from-emerald-500 to-emerald-600 text-[#0A0A0C]"
                      : "border border-white/15 text-slate-200 hover:border-emerald-500/40 hover:text-emerald-300"
                  }`}
                >
                  Empezar con {t.name}
                </a>
              </article>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};
